'use client';

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { getAuthHeaders } from '@/utils/apiHeaders';
import { getPublicApiBaseUrl } from '@/utils/publicApiBaseUrl';

export interface LlmConversation {
  id: string;
  title: string | null;
  source: string;
  source_id: string | null;
  status: string;
  created_at: string;
  last_message_at: string | null;
  updated_at: string;
  user_email: string | null;
}

interface UseLlmConversationsOptions {
  /** Filter listed conversations by source. Omit to list all sources. */
  source?: string;
  /** Source used when creating new conversations. Defaults to 'chat'. */
  createSource?: string;
}

export function useLlmConversations(options?: UseLlmConversationsOptions) {
  const source = options?.source;
  const createSource = options?.createSource ?? 'chat';
  const [conversations, setConversations] = useState<LlmConversation[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const base = getPublicApiBaseUrl();
      const url = source
        ? `${base}/private/llm/conversations?source=${source}`
        : `${base}/private/llm/conversations`;
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setConversations(data.data.conversations);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [source]);

  const createConversation = useCallback(
    async (
      title?: string,
      sourceOverride?: string,
      sourceId?: string,
      metadata?: Record<string, unknown>,
    ): Promise<LlmConversation | null> => {
      try {
        const response = await fetch(`${getPublicApiBaseUrl()}/private/llm/conversations`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            source: sourceOverride ?? createSource,
            title: title || 'New Chat',
            ...(sourceId ? { sourceId } : {}),
            ...(metadata ? { metadata } : {}),
          }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const conv: LlmConversation = data.data;
        setConversations((prev) => [conv, ...prev]);
        return conv;
      } catch (err) {
        console.error('Failed to create conversation:', err);
        toast.error('Failed to create conversation');
        return null;
      }
    },
    [createSource],
  );

  const deleteConversation = useCallback(
    async (id: string) => {
      // Optimistic removal
      setConversations((prev) => prev.filter((c) => c.id !== id));
      try {
        const response = await fetch(`${getPublicApiBaseUrl()}/private/llm/conversations/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          // Rollback on error
          await fetchConversations();
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (err) {
        console.error('Failed to delete conversation:', err);
        toast.error('Failed to delete conversation');
      }
    },
    [fetchConversations],
  );

  return {
    conversations,
    loading,
    fetchConversations,
    createConversation,
    deleteConversation,
  };
}
